import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { PersonRecord } from "../types/data";
import { v4 as uuidv4 } from "uuid";
import { useLang } from "../contexts/LangContext";
import { useUserData } from "../contexts/UserContext";
import CustomSelect from "../components/CustomSelect";
import AddressPicker, {
  AddressPickerHandle,
} from "../components/AddressPicker";
import { enableStickyWatcher } from "../utils/stickyWatcher";
import { motion } from "framer-motion";
import { storageService } from "../services/storage.service";
import { AppSettings } from "../types/data";
import { convertNumberInCurrentLang } from "../utils/misc";

export default function NewPerson() {
  const navigate = useNavigate();
  const addressPickerRef = useRef<AddressPickerHandle>(null);

  const { t, lang } = useLang();
  const { getUser, addPerson, appData } = useUserData();
  const user = getUser();
  // Use uuid or id as surveyor_id to match filtering in getSurveyedPersons()
  const surveyor_id = (user as any)?.uuid || user?.id || '';
  const currencySymbol = user?.channel_details?.currency_symbol || '৳';
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [age, setAge] = useState<number | "">("");
  const [income, setIncome] = useState<number | "">("");
  const [sex, setSex] = useState<"male" | "female" | "other" | undefined>(
    undefined
  );
  const [occupation, setOccupation] = useState<
    | "farmer"
    | "fisherman"
    | "government_officer"
    | "government_employee"
    | "private_service"
    | "business"
    | "teacher"
    | "student"
    | "housewife"
    | "other"
    | undefined
  >(undefined);

  const [address, setAddress] = useState({
    district: "",
    upazila: "",
    union: "",
    village: "",
  });
  const [fullAddress, setFullAddress] = useState("");

  const [addedOn] = useState(new Date().toISOString());
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // Scroll to top on mount instantly
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    enableStickyWatcher();
    
    // Load settings
    storageService.getSettings().then(setSettings).catch(console.error);
  }, []);

  function updateAddress(field: string, value: string) {
    setAddress((prev) => ({ ...prev, [field]: value }));
  }

  function updateAddressFromAddressPicker(addr: Record<string, string>) {
    setAddress({
      district: addr.district || "",
      upazila: addr.upazila || "",
      union: addr.union || "",
      village: addr.village || "",
    });
  }

  const commitAddress = () => {
    if ((settings?.surveyAddressType ?? 'bangladesh') === 'general') {
      // For general address, just return the full_address string
      return null; // We'll use full_address field instead
    }
    // For Bangladesh-specific address
    // On mobile, due to multilingual input issues, we need to commit the address picker
    addressPickerRef.current?.commit();
    const addr = addressPickerRef.current?.address || {};
    return {
      district: addr.district || "",
      upazila: addr.upazila || "",
      union: addr.union || "",
      village: addr.village || "",
    };
  };

  const createNewPerson = (): PersonRecord | null => {
    if (!name.trim()) {
      setError(t("requiredFields"));
      window.scrollTo(0, 0);
      return null;
    }

    // Check age only if the field is visible
    if ((settings?.surveyShowAge ?? true) && age === "") {
      setError(t("requiredFields"));
      window.scrollTo(0, 0);
      return null;
    }

    if ((settings?.surveyShowAge ?? true)) {
      const minAge = settings?.surveyMinAge ?? 0;
      const maxAge = settings?.surveyMaxAge ?? 120;
      const is_age_valid = Number.isInteger(age) && Number(age) >= minAge && Number(age) <= maxAge;
      if (!is_age_valid) {
        setError(t("invalidAge").replace("{min}", convertNumberInCurrentLang(lang, minAge)).replace("{max}", convertNumberInCurrentLang(lang, maxAge)));
        window.scrollTo(0, 0);
        return null;
      }
    }

    // Check if a person with the same mobile number already exists (only if mobile field is shown)
    if ((settings?.surveyShowMobile ?? false) && mobile.trim() !== "") {
      const existingPerson = appData?.persons?.find((p: PersonRecord) => p.mobile === mobile.trim());
      if (existingPerson) {
        setError(t("personWithMobileExists"));
        window.scrollTo(0, 0);
        return null;
      }
    }

    const new_address = commitAddress();

    const newPerson: PersonRecord = {
      version: 1,
      id: uuidv4(),
      name: name.trim(),
      age: (settings?.surveyShowAge ?? true) && age !== "" ? Number(age) : undefined,
      sex: (settings?.surveyShowSex ?? true) ? sex : undefined,
      mobile: (settings?.surveyShowMobile ?? false) ? mobile.trim() : undefined,
      income: (settings?.surveyShowIncome ?? false) && income !== "" ? Number(income) : undefined,
      occupation: (settings?.surveyShowOccupation ?? true) ? occupation : undefined,
      address: (settings?.surveyShowAddress ?? false) && (settings?.surveyAddressType ?? 'bangladesh') === 'bangladesh' ? new_address || undefined : undefined,
      full_address: (settings?.surveyShowAddress ?? false) && (settings?.surveyAddressType ?? 'bangladesh') === 'general' ? fullAddress.trim() || undefined : undefined,
      notes,
      added_on: addedOn,
      updated_at: addedOn,
      surveyor_id,
      records: [],
    };

    return newPerson;
  };

  const handleSubmit = () => {
    const person = createNewPerson();
    if (!person) return;

    addPerson(person);
    navigate("/survey/person-list");
  };

  const handleSubmitAndStart = () => {
    const person = createNewPerson();
    if (!person) return;

    addPerson(person);
    navigate(`/survey/new/${person.id}`);
  };

  function handleSubmitAndSkipSurvey(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    event.preventDefault();
    const person = createNewPerson();
    if (!person) return;

    addPerson(person);
    navigate("/survey/person-list");
  }

  return (
    <div
      className={
        "page relative flex flex-col grow pt-12 bg-transparent text-center"
      }
    >
      <div
        className="sticky-header sticky z-10 mb-4 px-scr py-4"
        data-watch-sticky
        data-sticky-top="auto"
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/survey')}
            className="p-1 rounded-full text-pink-600 bg-white/75 hover:bg-white transition-colors"
            aria-label={t("goBack")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="size-6" viewBox="0 0 24 24"><path fill="currentColor" d="M20 11.25a.75.75 0 0 1 0 1.5h-9.25V18a.75.75 0 0 1-1.28.53l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.28.53v5.25z" /></svg>
          </button>
          <h1><span className="text-pink-600">{t("addNewPerson")}</span></h1>
          <div className="w-8"></div>
        </div>        
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="space-y-4 my-8 px-scr max-w-lg mx-auto w-full"
      >
        {error && (
          <motion.div
            className="flex items-center justify-center text-center -mt-4 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <p className="bg-rose-700 text-white text-base rounded-full px-4 py-1">
              {error}
            </p>
          </motion.div>
        )}

        <div>
          <label className="block label">
            {(settings?.surveyNameFieldLabel ?? 'fullNameOrId') === 'idOnly' ? t("id") || "ID" : t("name")}
          </label>
          <input
            type="text"
            className="w-full input-base required"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
        </div>

        {(settings?.surveyShowMobile ?? false) && (
          <div className="!mt-8">
            <label className="block label">{t("mobile")}</label>
            <input
              type="text"
              className="w-full input-base"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />
          </div>
        )}

        {(settings?.surveyShowAge ?? true) && (
          <div className="!mt-8">
            <label className="block label">{t("age")}</label>
            <input
              type="number"
              min={settings?.surveyMinAge ?? 0}
              max={settings?.surveyMaxAge ?? 120}
              className="w-full input-base required"
              value={age}
              onChange={(e) => setAge(Number(e.target.value))}
              autoComplete="off"
              inputMode="numeric"
            />
          </div>
        )}

        {(settings?.surveyShowSex ?? true) && (
          <div className="!mt-8">
            <label className="block label">{t("sex")}</label>
            <CustomSelect
              value={sex}
              onChange={(value) => setSex(value as any)}
              options={[
                { label: t("male"), value: "male" },
                { label: t("female"), value: "female" },
                { label: t("other"), value: "other" },
              ]}
              placeholder={t("selectSex")}
            />
          </div>
        )}

        {(settings?.surveyShowOccupation ?? true) && (
          <div className="!mt-8">
            <label className="block label">{t("occupation")}</label>
            <CustomSelect
              value={occupation}
              onChange={(value) => setOccupation(value as any)}
              options={[
                { label: t("farmer"), value: "farmer" },
                { label: t("fisherman"), value: "fisherman" },
                { label: t("government_officer"), value: "government_officer" },
                { label: t("government_employee"), value: "government_employee" },
                { label: t("private_service"), value: "private_service" },
                { label: t("business"), value: "business" },
                { label: t("teacher"), value: "teacher" },
                { label: t("student"), value: "student" },
                { label: t("housewife"), value: "housewife" },
                { label: t("other"), value: "other" },
              ]}
              placeholder={t("selectOccupation")}
            />
          </div>
        )}

        {(settings?.surveyShowIncome ?? false) && (
          <div className="!mt-8">
            <label className="block label">{t("monthlyIncome")}</label>
            <input
              type="number"
              min="0"
              max="100000000"
              className="w-full input-base"
              value={income}
              onChange={(e) => setIncome(e.target.value === "" ? "" : Number(e.target.value))}
              autoComplete="off"
              inputMode="numeric"
              placeholder={`${currencySymbol} 0`}
            />
          </div>
        )}

        {(settings?.surveyShowAddress ?? false) && (
          <div className="!mt-8">
            <label className="block label">{t("address")}</label>
            {(settings?.surveyAddressType ?? 'bangladesh') === 'bangladesh' ? (
              <AddressPicker
                ref={addressPickerRef}
                onChange={(addr) => updateAddressFromAddressPicker(addr)}
              />
            ) : (
              <input
                type="text"
                className="w-full input-base"
                value={fullAddress}
                onChange={(e) => setFullAddress(e.target.value)}
                autoComplete="street-address"
                placeholder="Enter full address"
              />
            )}
          </div>
        )}

        <div className="!mt-8">
          <label className="block label">{t("notes")}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full input-base"
          />
        </div>

        <div className="flex items-center justify-center !mt-8">
          <button
            className="button-xs button-bg-grad-lightgray !text-gray-600"
            type="button"
            onClick={handleSubmitAndSkipSurvey}>
            {t("SaveAndSkipSurvey")}
          </button>
        </div>
      </form>

      <div className="bottom-nav-bar flex justify-center w-full sticky bottom-0 px-scr py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
        <button
          type="button"
          onClick={handleSubmitAndStart}
          className="button-base button-bg-grad-1"
        >
          {t("saveAndStart")}
        </button>
      </div>
    </div>
  );
}
